import { Injectable } from '@angular/core';
import { MarkdownService as NgxMarkdownService } from 'ngx-markdown';

import { ExportFilesFactory } from '@shared/services/file-export/drivers';
import {
  IFileExportDriver,
  TExportOptions,
  TFileExportConfiguration,
  TFileExportDriversAvailable,
} from '@shared/services/file-export/file-export-interface';

import { CommonService } from '@shared/services/common.service';
import { MermaidConverterService } from '@shared/services/mermaid-converter.service';
import { StringUtils } from '@shared/utils/stringUtils';

@Injectable({
  providedIn: 'root',
})
export class FileExportService {
  private pages: string[] = [];
  private messages: any[] = [];
  private isHtml = false;
  private driver: IFileExportDriver | undefined;

  constructor(
    private commonService: CommonService,
    private markdownService: NgxMarkdownService,
    private mermaidConverter: MermaidConverterService
  ) {}

  protected setPages(pages: string[] | undefined, isHtml?: boolean): FileExportService {
    if (pages) {
      this.pages = pages;
      this.messages = []; // Clear messages when setting pages
      this.isHtml = isHtml || false;
    }
    return this;
  }

  protected setMessages(messages: any[] | undefined): FileExportService {
    if (messages) {
      this.messages = messages;
      this.pages = []; // Clear pages when setting messages
      this.isHtml = true; // Messages are always processed as HTML
    }
    return this;
  }

  protected setDriver(driver: TFileExportDriversAvailable): FileExportService {
    this.driver = ExportFilesFactory.instantiateDriver(driver);
    return this;
  }

  protected async generateFile(options: TExportOptions, filename = 'results') {
    if (!this.driver) {
      throw new Error('No export driver selected');
    }
    this.commonService.setIsLoading(true);
    try {
      await this.driver.setLogo(StringUtils.brandResource('logoFile'));

      // Inject markdown service and Mermaid converter into drivers that support them (PDF drivers)
      if ('setMarkdownService' in this.driver) {
        (this.driver as any).setMarkdownService(this.markdownService);
      }
      if ('setMermaidConverter' in this.driver) {
        (this.driver as any).setMermaidConverter(this.mermaidConverter);
      }

      // Set either pages or messages based on what's available
      if (this.messages.length > 0) {
        if (this.driver.setMessages) {
          this.driver.setOptions(options);
          this.driver.setMessages(this.messages);
          await this.driver.generate(filename);
        } else {
          throw new Error('Driver does not support message format');
        }
      } else {
        await this.driver.setOptions(options).setPages(this.pages, this.isHtml).generate(filename);
      }

      this.driver.cleanUp();
    } catch (error) {
      console.error('Export generation failed:', error);
    } finally {
      this.commonService.setIsLoading(false);
    }
  }

  public getFile(data: TFileExportConfiguration, filename?: string) {
    if (data.messages) {
      this.setMessages(data.messages).setDriver(data.driver).generateFile(data.options, filename);
    } else {
      this.setPages(data.pages, data.isHtml)
        .setDriver(data.driver)
        .generateFile(data.options, filename);
    }
  }
}
